import React from "react";
import Axios from "axios";
import { getSessionId, init, logEvent, logException, setEntityId, setFeatureSetId } from "@src/utils/logClient";
import { checkRedirect } from "@src/utils/routes";
import followChannel from "@src/services/ideas/followChannel";
import getFollowingChannels from "@src/services/ideas/getFollowingChannels";
import unfollowChannel from "@src/services/ideas/unfollowChannel";
import cookies from "cookies-js";
import getClasses from "@src/services/ideas/getClasses";
import shareToClassStory from "@src/services/ideas/shareToClassStory";
import _get from "lodash/get";
import window from "global/window";
import { FEATURE_SWITCH_ID } from "@src/utils/cookies";
import { APIDomain } from "@src/utils/APIDomain";
import { isProduction } from "@src/utils/env";
import { EntityType } from "@src/utils/getSubdomainURL";

const entityMap = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
  "/": "&#x2F;",
};

const escapeHtml = function (str) {
  // eslint-disable-next-line no-useless-escape
  return String(str).replace(/[&<>"'\/]/g, function (s) {
    return entityMap[s];
  });
};

const isFromUs = function (headers) {
  const header = headers["x-usa"] || headers["X-USA"];
  return header && Number(header) === 1;
};

const isBadParentCodePath = (path) =>
  /^[pP]{1}.+/.test(path) && path !== "proudtoteach" && path !== "privacy" && path !== "playvideo";

const setOnError = () => {
  window.onerror = async function (msg, url, lineNo, colNo, error: any) {
    let context;
    if (!url || /classdojo/gi.test(url)) {
      context = "script: " + url + ", lineNo: " + lineNo + ", colNo: " + colNo;
      if (!error) {
        error = {
          message: msg,
          status: "unknown",
          method: "unknown",
          url: url,
          stack: context,
        };
      }
      if (Math.random() < 0.1) {
        await logException(error, context);
      }
    }
  };
};

type AppData = {
  loadingSession?: boolean;
  type?: EntityType;
  fromUS?: boolean;
  needsDataTransferConsent?: boolean;
  userData?: Partial<{ type: EntityType }>;
  name?: string;
  followingChannels: Array<any>;
  classes: Array<any>;
  shareStatus: { [key: string]: string };
  featureFlags: any;
  services?: {
    followChannel?: (string) => Record<string, unknown>;
    unfollowChannel?: (string) => Record<string, unknown>;
    getFollowingChannels?: () => Record<string, unknown>;
    logout?: () => void;
    getSession?: () => void;
    getClasses?: () => void;
    shareToClassStory?: (classId: string, body: string, image: string) => void;
    getFeatureFlags?: () => void;
  };
};
type AppDataContextProps = { data: AppData; set: (AppData) => void };

const defaultContextValue: AppDataContextProps = {
  data: {
    loadingSession: true,
    type: undefined,
    fromUS: undefined,
    needsDataTransferConsent: true,
    userData: undefined,
    featureFlags: {},
    followingChannels: [],
    shareStatus: {},
    classes: [],
    services: {},
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  set: () => {},
};

const AppDataContext = React.createContext<AppDataContextProps>(defaultContextValue);

class AppDataContextProvider extends React.Component<Record<string, unknown>, AppDataContextProps> {
  constructor(props) {
    super(props);

    this.setData = this.setData.bind(this);

    defaultContextValue.data.services.getFollowingChannels = async () => {
      try {
        const channels = await getFollowingChannels();
        this.setData({
          followingChannels: channels.status === 200 ? channels.data["_items"] : [],
        });
        return channels;
      } catch (e) {
        console.error("Failed getting followed channels");
      }
    };

    defaultContextValue.data.services.followChannel = async (channel) => {
      const result = await followChannel(channel);
      defaultContextValue.data.services.getFollowingChannels();
      return result;
    };

    defaultContextValue.data.services.unfollowChannel = async (channel) => {
      const result = await unfollowChannel(channel);
      defaultContextValue.data.services.getFollowingChannels();
      return result;
    };

    defaultContextValue.data.services.logout = () => {
      const SESSION = `${APIDomain}/api/session`;
      Axios.delete(SESSION, { withCredentials: true }).then(() => {
        this.setData({
          userData: undefined,
          loadingSession: false,
          type: undefined,
          fromUS: undefined,
          classes: [],
          followingChannels: [],
          shareStatus: {},
        });
      });
    };

    defaultContextValue.data.services.getFeatureFlags = async () => {
      const cookieKey = FEATURE_SWITCH_ID;
      const flagId = cookies.get(cookieKey);
      const locale = cookies.get("dojoDesiredLanguage") || "en-us";
      const sessionId = await (window as any).sessionId;
      const userId = _get(this.state, "data.userData._id");
      const switches = "entitySwitches=externalSiteTest"; //add all external site switches here in the future
      let flagUrl = `${APIDomain}/api/featureSwitch?${switches}`;
      if (flagId) {
        flagUrl += `&featureSetId=${flagId}`;
      }
      if (userId) {
        flagUrl += `&entityId=${userId}`;
      } else if (sessionId) {
        flagUrl += `&entityId=${sessionId}`;
      }

      Axios.get(flagUrl)
        .then((result) => {
          const featureSetId = result.data.featureSetId;
          if (featureSetId) {
            setFeatureSetId(featureSetId);
          }
          this.setData({ featureFlags: result.data.entity });
        })
        .catch((e) => {
          console.log(e);
        });
    };

    defaultContextValue.data.services.getSession = async () => {
      this.getSession().finally(() => {
        defaultContextValue.data.services.getFeatureFlags();

        if (process.env.GATSBY_BUILD_WEBSITE === "ideas") {
          defaultContextValue.data.services.getClasses();
          defaultContextValue.data.services.getFollowingChannels();
        }
      });
    };

    defaultContextValue.data.services.getClasses = async () => {
      try {
        const result = await getClasses();
        this.setData({
          classes: result,
        });
      } catch (e) {
        console.error("Failed getting classes");
      }
    };

    defaultContextValue.data.services.shareToClassStory = (classId, body, image) => {
      this.setData({
        shareStatus: Object.assign({}, this.state.data.shareStatus, { [classId]: "pending" }),
      });
      const result = shareToClassStory(classId, body, image)
        .then(() => {
          this.setData({
            shareStatus: Object.assign({}, this.state.data.shareStatus, { [classId]: "done" }),
          });
        })
        .catch(() => {
          this.setData({
            shareStatus: Object.assign({}, this.state.data.shareStatus, { [classId]: "error" }),
          });
        });
    };

    this.state = {
      ...defaultContextValue,
      set: this.setData,
    };
  }

  componentDidMount() {
    if (process.env.GATSBY_BUILD_WEBSITE === "www") {
      checkRedirect(); //redirect to desired locale
    }
    let site = process.env.GATSBY_BUILD_WEBSITE;
    if (process.env.GATSBY_BUILD_WEBSITE === "www") {
      site = "external";
    } else if (process.env.GATSBY_BUILD_WEBSITE === "ideas") {
      site = "external-ideas";
    }

    if (process.env.GATSBY_ENV === "development") {
      init({
        site,
        env: "dev",
        customEndpoint: "/logs",
      });
    } else {
      init({
        site,
        env: "prod",
        customEndpoint: "/logs",
        buildNumber: `${process.env.GATSBY_BUILD_NUMBER}-${process.env.GATSBY_GIT_COMMIT}`,
      });
    }

    if (typeof window !== "undefined") {
      (window as any).sessionId = getSessionId();
    }

    setOnError();

    this.getNeedsDataTransferConsent();

    this.getSession().finally(() => {
      this.state.data.services.getFeatureFlags();

      if (process.env.GATSBY_BUILD_WEBSITE === "ideas") {
        this.state.data.services.getFollowingChannels();
        this.state.data.services.getClasses();
      }
    });
  }

  getNeedsDataTransferConsent() {
    return Axios.get(`${APIDomain || ""}/api/needsDataTransferConsent`)
      .then((response) => {
        this.setData({
          needsDataTransferConsent: response.data.needsDataTransferConsent,
        });
      })
      .catch((error) => {
        console.error(
          `Failed getting needsDataTransferConsent value to determine users need to provide consent on signup: ${error}`,
        );
      });
  }

  getSession() {
    const SESSION = `${APIDomain || ""}/api/session`;
    return Axios.get(SESSION, { withCredentials: true })
      .then((response) => {
        const data: Partial<AppData> = { fromUS: isFromUs(response.headers) };
        const userData = response.data || {};
        if ("type" in userData) {
          data.type = userData.type;
          if (userData.type === "teacher") {
            data.userData = userData.teacher;
            data.name = escapeHtml(userData.teacher.firstName);
          } else if (userData.type === "parent") {
            data.userData = userData.parent;
            data.name = escapeHtml(userData.parent.firstName);
          } else if (userData.student) {
            data.userData = userData.student;
            data.name = userData.student.firstName ? escapeHtml(userData.student.firstName) : "";
          }
        }
        data.userData.type = data.type;
        this.setData(data);

        const entity = userData.teacher || userData.parent || userData.student;
        setEntityId(entity._id);
        const routeName = window.location.pathname.slice(1).split("/").shift() || "index";
        if (isBadParentCodePath(routeName)) logEvent("user_visit:bad_parent_code_path");
        else logEvent(`user_visit:${routeName}`);
      })
      .catch((error) => {
        // if (error && !isProduction()) throw error;

        this.setData({ fromUS: error.response ? isFromUs(error.response.headers) : false });
      })
      .finally(() => {
        this.setData({ loadingSession: false });
      });
  }

  setData(newData) {
    this.setState((state: AppDataContextProps) => ({
      data: {
        ...state.data,
        ...newData,
      },
    }));
  }

  render() {
    return <AppDataContext.Provider value={this.state}>{this.props.children}</AppDataContext.Provider>;
  }
}

export { AppDataContext, AppDataContextProvider };
